import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
} from '@material-ui/core';
import attachFilesUtil from '@src/components/ChatMessageComposer/ComposerToolbar/FileUtils';
import useAttachFilesSummary from '@src/hooks/useAttachFilesSummary';
import { activeChatSelector } from '@src/selectors/chats';
import { RootState } from '@src/store/reduxstore';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

interface IImagePreviewDialog {
  openImagePreview: boolean;
  pastedImageFile: File | null;
  handleImagePreviewClose: () => void;
}

const ImagePreviewDialogWrapper: React.FC = () => {
  const [openImagePreview, setOpenImagePreview] = useState(false);
  const [pastedImageFile, setPastedImageFile] = useState<File | null>(null);

  const isFileSubmissionEnabled = useSelector(
    (state: RootState) => state.generalProperties.isFileSubmissionEnabled
  );

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && openImagePreview === true) {
      setOpenImagePreview(false);
    }
  };

  useEffect(() => {
    document.onpaste = pasteEvent => {
      const { files } = pasteEvent.clipboardData as DataTransfer;
      const fileCopied = files[0];

      if (
        isFileSubmissionEnabled &&
        fileCopied &&
        fileCopied.type.indexOf('image') === 0
      ) {
        const fileType = fileCopied.type.split('/')[1];
        const newImage = new window.File(
          [fileCopied],
          `clipboard-image.${fileType}`,
          {
            type: fileCopied.type,
          }
        );

        setOpenImagePreview(true);
        setPastedImageFile(newImage);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  });

  return (
    <ImagePreviewDialog
      openImagePreview={openImagePreview}
      pastedImageFile={pastedImageFile}
      handleImagePreviewClose={() => setOpenImagePreview(false)}
    />
  );
};

const ImagePreviewDialog: React.FC<IImagePreviewDialog> = ({
  openImagePreview,
  pastedImageFile,
  handleImagePreviewClose,
}) => {
  const { formatMessage } = useIntl();

  const { isAttachFilesSummaryEnabled } = useAttachFilesSummary();

  const activeChat = useSelector(activeChatSelector);

  const handleImagePreviewSend = () => {
    attachFilesUtil({
      files: pastedImageFile ? [pastedImageFile] : [],
      activeChat,
      formatMessage,
      isAttachFilesSummaryEnabled,
    });

    handleImagePreviewClose();
  };

  const createImagePreviewURL = () => {
    const URLimage = window.URL || window.webkitURL;
    return pastedImageFile ? URLimage.createObjectURL(pastedImageFile) : '';
  };

  return (
    <Dialog open={openImagePreview}>
      <DialogContent>
        <img
          src={createImagePreviewURL()}
          style={{ width: '100%', height: '100%', maxHeight: '450px' }}
          alt="Preview"
        />
      </DialogContent>

      <DialogActions>
        <Button
          color="primary"
          variant="outlined"
          onClick={handleImagePreviewClose}
        >
          {formatMessage({ id: 'ChatEvent.PasteImage.cancel' })}
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={handleImagePreviewSend}
        >
          {formatMessage({
            id: 'ChatEvent.PasteImage.send',
          })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImagePreviewDialogWrapper;
